import React, { useState, useMemo, useEffect, useRef, useCallback } from 'react';
import { useNavigate, Link as RouterLink, useSearchParams, useLocation } from 'react-router-dom';
import {
  Box,
  TextField,
  Button,
  Typography,
  Alert,
  InputAdornment,
  IconButton,
  Link,
  Autocomplete,
} from '@mui/material';
import { Country, State } from 'country-state-city';
import PhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/style.css';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { GoogleSignInButton } from '../components/GoogleSignInButton';
import { AuthPageShell } from '../components/auth/AuthPageShell';
import { useAuthFormToast } from '../components/auth/useAuthFormToast';
import { showAuthErrorToast } from '../utils/authConfigurationErrors';
import { useAuthGoogleConfigToast } from '../hooks/useAuthGoogleConfigToast';
import {
  authDividerRowSx,
  authFieldSx,
  authLinkSx,
  authPrimaryButtonSx,
  authSectionLabelSx,
  phoneInputStyles,
} from '../components/auth/authFormStyles';
import { responseRequiresVerification } from '../utils/pendingVerification';
import { deriveProfileComplete } from '../utils/profileCompletion';
import {
  clearPendingSignupDraft,
  getPendingSignupDraft,
  savePendingSignupDraft,
} from '../utils/pendingSignupDraft';
import { parseSignupError } from '../utils/signupErrors';

export const SignUpPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { signUp, signInWithGoogle, setPendingProfileUser } = useAuth();

  const redirectPath = useMemo(() => {
    const raw = searchParams.get('redirect');
    if (!raw) return '/';
    try {
      const decoded = decodeURIComponent(raw);
      return decoded.startsWith('/') ? decoded : '/';
    } catch {
      return '/';
    }
  }, [searchParams]);

  const isInviteJoin = redirectPath.includes('/community/join/');
  const changeEmail = Boolean((location.state as { changeEmail?: boolean } | null)?.changeEmail);
  const loginHref =
    redirectPath !== '/'
      ? `/login?redirect=${encodeURIComponent(redirectPath)}`
      : '/login';

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    confirmPassword: '',
  });
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [emailError, setEmailError] = useState('');
  const { showError, ToastHost } = useAuthFormToast();
  useAuthGoogleConfigToast(showError);
  const [isDuplicateEmail, setIsDuplicateEmail] = useState(false);
  const [loading, setLoading] = useState(false);
  const [country, setCountry] = useState<{ isoCode: string; name: string } | null>(null);
  const [state, setState] = useState<{ isoCode: string; name: string } | null>(null);
  const [phoneNumber, setPhoneNumber] = useState('');

  const countries = useMemo(() => Country.getAllCountries(), []);
  const states = useMemo(() => State.getStatesOfCountry(country?.isoCode ?? ''), [country]);
  const draftRestoredRef = useRef(false);

  useEffect(() => {
    if (draftRestoredRef.current) return;
    draftRestoredRef.current = true;

    const draft = getPendingSignupDraft();
    if (!draft) return;

    const changeEmailOnRestore = Boolean((location.state as { changeEmail?: boolean } | null)?.changeEmail);
    const matchedCountry = draft.countryIso
      ? countries.find((c) => c.isoCode === draft.countryIso)
      : null;
    const matchedState =
      draft.countryIso && draft.stateIso
        ? State.getStatesOfCountry(draft.countryIso).find((s) => s.isoCode === draft.stateIso)
        : null;

    setFormData({
      name: draft.name,
      email: changeEmailOnRestore ? '' : draft.email,
      password: draft.password,
      confirmPassword: draft.confirmPassword,
    });
    setPhoneNumber(draft.phoneNumber);
    setCountry(matchedCountry ?? null);
    setState(matchedState ?? null);
  }, [countries, location.state]);

  useEffect(() => {
    const hasContent =
      formData.name.trim() ||
      formData.email.trim() ||
      formData.password ||
      phoneNumber ||
      country ||
      state;
    if (!hasContent) return;

    const timer = window.setTimeout(() => {
      savePendingSignupDraft({
        name: formData.name,
        email: formData.email,
        password: formData.password,
        confirmPassword: formData.confirmPassword,
        phoneNumber,
        countryIso: country?.isoCode ?? '',
        stateIso: state?.isoCode ?? '',
      });
    }, 300);

    return () => window.clearTimeout(timer);
  }, [formData, phoneNumber, country, state]);

  const handleChange = (field: string) => (e: React.ChangeEvent<HTMLInputElement>) => {
    if (field === 'email') {
      setEmailError('');
      setIsDuplicateEmail(false);
    }
    setFormData((prev) => ({ ...prev, [field]: e.target.value }));
  };

  const isFormValid =
    formData.name.trim() &&
    formData.email.trim() &&
    formData.password.length >= 6 &&
    formData.password === formData.confirmPassword &&
    country &&
    state &&
    phoneNumber.replace(/\D/g, '').length >= 7;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setEmailError('');
    setIsDuplicateEmail(false);

    if (formData.password !== formData.confirmPassword) {
      showError('Passwords do not match');
      return;
    }
    if (formData.password.length < 6) {
      showError('Password must be at least 6 characters');
      return;
    }

    setLoading(true);
    try {
      const signupPayload = {
        name: formData.name.trim(),
        email: formData.email.trim(),
        password: formData.password,
        phone: phoneNumber,
        country: country?.isoCode,
        state: state?.isoCode,
      };

      savePendingSignupDraft({
        name: signupPayload.name,
        email: signupPayload.email,
        password: formData.password,
        confirmPassword: formData.confirmPassword,
        phoneNumber,
        countryIso: country?.isoCode ?? '',
        stateIso: state?.isoCode ?? '',
      });

      const response = await signUp(signupPayload);

      if (responseRequiresVerification(response)) {
        navigate('/verify-otp', {
          state: { email: formData.email.trim() },
          replace: true,
        });
        return;
      }

      if (!deriveProfileComplete(response?.client)) {
        clearPendingSignupDraft();
        navigate('/complete-profile', { replace: true });
      } else {
        clearPendingSignupDraft();
        navigate(redirectPath);
      }
    } catch (err: unknown) {
      const { message, isDuplicateEmail: duplicateEmail } = parseSignupError(err);
      showAuthErrorToast(showError, message, message);
      setIsDuplicateEmail(duplicateEmail);
      if (duplicateEmail) {
        setEmailError(message);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleSignIn = useCallback(
    async (credential: string) => {
      setLoading(true);
      try {
        const response = await signInWithGoogle(credential);
        if (!response.hasCompletedProfile) {
          setPendingProfileUser({ isNewSocialUser: response.isNewSocialUser });
          navigate('/complete-profile');
        } else {
          navigate(redirectPath);
        }
      } catch (err: unknown) {
        showAuthErrorToast(showError, err, 'Google sign-up failed. Please try again.');
        setLoading(false);
      }
    },
    [signInWithGoogle, setPendingProfileUser, navigate, redirectPath, showError]
  );

  const handleGoogleError = useCallback(
    (err: Error) => {
      showAuthErrorToast(showError, err, 'Google sign-up failed.');
    },
    [showError]
  );

  return (
    <>
    <AuthPageShell
      title={isInviteJoin ? 'Create account to join' : 'Create your account'}
      subtitle="We'll send a verification code to your email before you can sign in."
      maxWidth={480}
    >
      {changeEmail && (
        <Alert severity="info" sx={{ mb: 2, width: '100%', borderRadius: 2 }}>
          Your name, phone, and location are saved — enter a new email below to continue.
        </Alert>
      )}

      {isInviteJoin && (
        <Alert severity="info" sx={{ mb: 2, width: '100%', borderRadius: 2 }}>
          Sign up below to accept this community invite.
        </Alert>
      )}

      {isDuplicateEmail && (
        <Typography variant="body2" sx={{ mb: 2, width: '100%', color: '#565959', textAlign: 'center' }}>
          Already have an account?{' '}
          <Link component={RouterLink} to={loginHref} sx={authLinkSx}>
            Sign in here
          </Link>
        </Typography>
      )}

      <Box component="form" onSubmit={handleSubmit} sx={{ width: '100%' }}>
        <Typography sx={authSectionLabelSx}>Account</Typography>

        <TextField
          fullWidth
          label="Full name"
          value={formData.name}
          onChange={handleChange('name')}
          variant="outlined"
          autoComplete="name"
          autoFocus={!changeEmail}
          required
          sx={authFieldSx}
        />

        <TextField
          fullWidth
          label="Email"
          type="email"
          value={formData.email}
          onChange={handleChange('email')}
          variant="outlined"
          autoComplete="email"
          autoFocus={changeEmail}
          required
          error={Boolean(emailError)}
          helperText={emailError || undefined}
          sx={authFieldSx}
        />

        <TextField
          fullWidth
          label="Password"
          type={showPassword ? 'text' : 'password'}
          value={formData.password}
          onChange={handleChange('password')}
          variant="outlined"
          autoComplete="new-password"
          required
          helperText="At least 6 characters"
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton onClick={() => setShowPassword(!showPassword)} edge="end">
                  {showPassword ? <VisibilityOff /> : <Visibility />}
                </IconButton>
              </InputAdornment>
            ),
          }}
          sx={authFieldSx}
        />

        <TextField
          fullWidth
          label="Confirm password"
          type={showConfirmPassword ? 'text' : 'password'}
          value={formData.confirmPassword}
          onChange={handleChange('confirmPassword')}
          variant="outlined"
          autoComplete="new-password"
          required
          error={Boolean(formData.confirmPassword && formData.password !== formData.confirmPassword)}
          helperText={
            formData.confirmPassword && formData.password !== formData.confirmPassword
              ? 'Passwords do not match'
              : ' '
          }
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton onClick={() => setShowConfirmPassword(!showConfirmPassword)} edge="end">
                  {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                </IconButton>
              </InputAdornment>
            ),
          }}
          sx={authFieldSx}
        />

        <Typography sx={authSectionLabelSx}>Contact details</Typography>

        <Box sx={{ mb: 2 }}>
          <Typography variant="caption" color="text.secondary" sx={{ mb: 0.75, display: 'block' }}>
            Phone number *
          </Typography>
          <PhoneInput
            country={country?.isoCode.toLowerCase() ?? 'ca'}
            value={phoneNumber}
            onChange={setPhoneNumber}
            enableSearch
            searchPlaceholder="Search country…"
            {...phoneInputStyles}
          />
        </Box>

        <Autocomplete
          options={countries}
          getOptionLabel={(option) => option.name}
          isOptionEqualToValue={(option, value) => option.isoCode === value.isoCode}
          value={country}
          onChange={(_, newValue) => {
            setCountry(newValue);
            setState(null);
          }}
          renderInput={(params) => (
            <TextField {...params} label="Country" required variant="outlined" />
          )}
          sx={authFieldSx}
        />

        <Autocomplete
          options={states}
          disabled={!country}
          getOptionLabel={(option) => option.name}
          isOptionEqualToValue={(option, value) => option.isoCode === value.isoCode}
          value={state}
          onChange={(_, newValue) => setState(newValue)}
          renderInput={(params) => (
            <TextField {...params} label="State / Province" required variant="outlined" />
          )}
          sx={{ ...authFieldSx, mb: 3 }}
        />

        <Button
          type="submit"
          fullWidth
          variant="contained"
          disabled={loading || !isFormValid}
          sx={authPrimaryButtonSx}
        >
          {loading ? 'Creating account…' : 'Create account & verify email'}
        </Button>
      </Box>

      <Box sx={authDividerRowSx}>
        <Box sx={{ flex: 1, height: '1px', bgcolor: '#E8EAED' }} />
        <Typography variant="body2" sx={{ mx: 2, color: '#565959', fontSize: '0.8rem' }}>
          or continue with
        </Typography>
        <Box sx={{ flex: 1, height: '1px', bgcolor: '#E8EAED' }} />
      </Box>

      <Box sx={{ width: '100%', mb: 2.5 }}>
        <GoogleSignInButton
          onSuccess={handleGoogleSignIn}
          onError={handleGoogleError}
          text="signup_with"
        />
      </Box>

      <Typography variant="body2" sx={{ color: '#565959', textAlign: 'center' }}>
        Already have an account?{' '}
        <Link component={RouterLink} to={loginHref} sx={authLinkSx}>
          Sign in
        </Link>
      </Typography>
    </AuthPageShell>
    <ToastHost />
    </>
  );
};
